import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  Input,
  OnInit,
  output,
  ViewChild
} from '@angular/core';
import { ENTER } from '@angular/cdk/keycodes';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IdShortNameWidgetResponse } from '@app/models/response.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';

@UntilDestroy()
@Component({
  selector: 'app-select-chip-input',
  templateUrl: './select-chip-input.component.html',
  styleUrls: ['./select-chip-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatFormFieldModule,
    MatChipsModule,
    MatAutocompleteModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule
  ]
})
export class SelectChipInputComponent implements OnInit {
  separatorKeysCodes: number[] = [ENTER];
  inputCtrl = new FormControl();

  allChips = input<IdShortNameWidgetResponse[]>();
  isError = input<boolean>(false);
  @Input() selectedChips: IdShortNameWidgetResponse[] = [];

  selectionChanged = output<IdShortNameWidgetResponse[]>();
  selectedChip = output<IdShortNameWidgetResponse>();
  valueChanged = output<string>();
  removedChip = output<number>();
  blurEmitter = output<void>();

  @ViewChild('chipInput') chipInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) trigger: MatAutocompleteTrigger;

  searchSubject: Subject<string> = new Subject<string>();

  ngOnInit(): void {
    this.subscribeToSearchChanges();
  }

  private subscribeToSearchChanges(): void {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe((search: string) => this.valueChanged.emit(search));
  }

  onClick(): void {
    this.trigger._onChange('');
    this.trigger.openPanel();
  }

  onBlur(): void {
    this.chipInput.nativeElement.value = '';
    this.blurEmitter.emit();
  }

  onInput(event): void {
    const value = event.target.value.trim();
    if (!value) {
      this.chipInput.nativeElement.value = '';
    }
    this.searchSubject.next(value);
  }

  add(event: MatChipInputEvent, input): void {
    const value = (event.value || '').trim();

    // Add a chip
    if (value) {
      const chip = this.allChips().find(chip => chip.short_name === value);
      if (chip) {
        this.selectedChips.push(chip);
      }
    }

    // Clear the input value
    input.value = '';

    this.inputCtrl.setValue(null);
  }

  remove(chipId: number): void {
    const index = this.selectedChips.findIndex(c => c.id === chipId);

    if (index >= 0) {
      this.selectedChips.splice(index, 1);
      this.selectionChanged.emit(this.selectedChips);
      this.removedChip.emit(chipId);
    }
    this.inputCtrl.updateValueAndValidity();
  }

  isDisabled(chipId: number): boolean {
    return this.selectedChips.some(chip => chip.id === chipId);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    const foundChip = this.allChips().find(chip => chip.id === event.option.value);
    this.selectedChips.push(foundChip);
    this.selectedChip.emit(foundChip);
    this.chipInput.nativeElement.value = '';
    this.inputCtrl.setValue(null);
    this.selectionChanged.emit(this.selectedChips);
  }
}
